import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface DigitalSectionProps {
  digitalImpressions: number;
  digitalCpm: number;
  onDigitalImpressionsChange: (value: number) => void;
  onDigitalCpmChange: (value: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function DigitalSection({
  digitalImpressions,
  digitalCpm,
  onDigitalImpressionsChange,
  onDigitalCpmChange,
  canEdit,
  numberFormat = 'eu',
}: DigitalSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [expanded, expand] = useBlankStart(digitalImpressions !== 0 || digitalCpm !== 0);

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Digital Display
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Digital display advertising impressions and rates.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      {!expanded && (
        <div className="max-w-2xl md:col-span-2">
          <AddEntryButton label="Digital Display" onClick={expand} disabled={!canEdit} />
        </div>
      )}

      {expanded && (
        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          {/* Digital Impressions */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Digital Impressions
                  <Tooltip {...tooltipData.digitalImpressions} wide={true} />
                </>
              }
              value={digitalImpressions}
              onChange={(value) =>
                onDigitalImpressionsChange(typeof value === 'number' ? value : 0)
              }
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Digital CPM */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Digital CPM
                  <Tooltip {...tooltipData.digitalCpm} wide={true} />
                </>
              }
              value={digitalCpm}
              onChange={(value) => onDigitalCpmChange(typeof value === 'number' ? value : 0)}
              decimals={2}
              numberFormat={numberFormat}
              suffix={getCurrencySymbol(baseCurrency)}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
}
